import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import { adminSignup, isAdminLoggedIn, setAdminSession } from "../api/adminClient";
import { uploadTenantLogo } from "./uploadTenantLogo";
import { useTheme } from "../context/ThemeContext";
import { ROUTES } from "./routes";
import "./login.css";
import "./onboarding.css";

const STEPS = [
  { id: 1, label: "Organization", hint: "Name your workspace" },
  { id: 2, label: "Admin account", hint: "Set up your login" },
];

const SETUP_CHECKLIST = [
  "Dedicated tenant boundary for all data",
  "Company Admin role with full workspace access",
  "Add portfolio companies in Studio after signup",
  "NEXUS dashboard unlocked immediately",
];

function IconSun({ className = "w-4 h-4" }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  );
}

function IconMoon({ className = "w-4 h-4" }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"
      />
    </svg>
  );
}

function StepProgress({ step, orgName }) {
  const progress = step === 1 ? 50 : 100;
  return (
    <div className="admin-onboard-progress-wrap">
      <div className="admin-onboard-progress-track" aria-hidden>
        <div className="admin-onboard-progress-fill" style={{ width: `${progress}%` }} />
      </div>
      <ol className="admin-onboard-steps" aria-label="Setup progress">
        {STEPS.map((s) => {
          const done = step > s.id;
          const active = step === s.id;
          return (
            <li
              key={s.id}
              className={`admin-onboard-step${active ? " admin-onboard-step--active" : ""}${
                done ? " admin-onboard-step--done" : ""
              }`}
            >
              <span className="admin-onboard-step-num">{done ? "✓" : s.id}</span>
              <div className="admin-onboard-step-text">
                <span className="admin-onboard-step-label">{s.label}</span>
                <span className="admin-onboard-step-hint">
                  {active && s.id === 1 && orgName.trim()
                    ? orgName.trim()
                    : s.hint}
                </span>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const dark = theme === "dark";
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoUrl, setLogoUrl] = useState("");
  const [logoUploading, setLogoUploading] = useState(false);
  const [form, setForm] = useState({
    organization_name: "",
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirm_password: "",
  });

  useEffect(() => {
    if (isAdminLoggedIn()) navigate(ROUTES.dashboard, { replace: true });
  }, [navigate]);

  const updateField = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const canContinueStep1 = form.organization_name.trim().length >= 2;
  const passwordReady = form.password.length >= 8 && form.password === form.confirm_password;

  const handleStep1 = (e) => {
    e.preventDefault();
    if (!canContinueStep1) {
      toast.error("Enter your organization name (at least 2 characters)");
      return;
    }
    setStep(2);
  };

  const handleLogoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const preview = URL.createObjectURL(file);
      setLogoPreview(preview);
      const url = await uploadTenantLogo(file);
      setLogoUrl(url);
      toast.success("Logo ready");
    } catch (err) {
      toast.error(err.message || "Logo upload failed");
      setLogoPreview(null);
      setLogoUrl("");
    } finally {
      setLogoUploading(false);
      e.target.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (form.password !== form.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const res = await adminSignup({
        organization_name: form.organization_name.trim(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        password: form.password,
        logo_url: logoUrl || undefined,
      });
      const user = res.data.user;
      user.name = `${user.first_name || ""} ${user.last_name || ""}`.trim();
      setAdminSession(res.data.token, user);
      toast.success(`Welcome to ${res.data.tenant_name || "your workspace"}`);
      navigate(ROUTES.dashboard);
    } catch (err) {
      toast.error(err.message || "Could not create organization");
    } finally {
      setLoading(false);
    }
  };

  const heroTitle =
    step === 1
      ? (
          <>
            Launch your
            <span className="admin-onboard-headline-accent"> private workspace</span>
          </>
        )
      : (
          <>
            Create your
            <span className="admin-onboard-headline-accent"> admin account</span>
          </>
        );

  const heroLead =
    step === 1
      ? "Each organization gets its own isolated environment. Your team's companies, invites, analytics, and reports never overlap with other customers."
      : "You'll be the first admin for this workspace. Invite teammates and add portfolio companies after signup.";

  return (
    <div className="admin-login-glass-page admin-onboard-glass-page" data-theme={theme}>
      <Toaster position="top-center" />

      <div className="admin-login-glass-bg" aria-hidden>
        <div className="admin-login-glass-grid" />
        <div className="admin-login-glass-glow admin-onboard-glow--brand" />
        <div className="admin-login-glass-glow admin-onboard-glow--cyan" />
      </div>

      <header className="admin-login-glass-header">
        <Link to={ROUTES.login} className="admin-login-glass-brand">
          <img src="/5x-logo-tagline.png" alt="5X Assessment" className="admin-login-glass-logo" />
        </Link>
        <nav className="admin-login-glass-nav" aria-label="Signup navigation">
          <Link to={ROUTES.login} className="admin-login-glass-nav-link">Sign in</Link>
          <button
            type="button"
            className="admin-login-glass-theme"
            onClick={toggleTheme}
            aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
          >
            {dark ? <IconSun /> : <IconMoon />}
          </button>
        </nav>
      </header>

      <main className="admin-login-glass-main admin-onboard-glass-main">
        <section className="admin-onboard-hero" aria-labelledby="onboard-hero-title">
          <p className="admin-onboard-eyebrow">New workspace</p>
          <h1 id="onboard-hero-title" className="admin-onboard-headline">{heroTitle}</h1>
          <p className="admin-onboard-lead">{heroLead}</p>

          <StepProgress step={step} orgName={form.organization_name} />

          <ul className="admin-onboard-checklist">
            {SETUP_CHECKLIST.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </section>

        <section className="admin-login-glass-card-wrap">
          {step === 1 ? (
            <form onSubmit={handleStep1} className="admin-login-glass-card admin-onboard-card">
              <p className="admin-onboard-card-step">Step 1 of 2</p>
              <h2 className="admin-login-glass-card-title">Name your organization</h2>
              <p className="admin-login-glass-card-sub">
                This becomes your workspace label in the sidebar and scopes all data to your team.
              </p>

              <label className="admin-login-glass-label" htmlFor="onboard-org-name">
                Organization name
              </label>
              <input
                id="onboard-org-name"
                type="text"
                className="admin-login-glass-input admin-onboard-input-full"
                value={form.organization_name}
                onChange={updateField("organization_name")}
                placeholder="e.g. Acme Consulting, GLP-1 Partners"
                required
                minLength={2}
                autoFocus
              />

              <div className="admin-onboard-tip">
                <strong>Tip:</strong> Use your firm or company name — you can add portfolio companies
                inside the workspace after signup.
              </div>

              <div className="admin-onboard-logo-row">
                <label htmlFor="onboard-org-logo" className="admin-onboard-logo-preview">
                  {logoPreview || logoUrl ? (
                    <img src={logoPreview || logoUrl} alt="" />
                  ) : (
                    <span>Logo</span>
                  )}
                </label>
                <div className="admin-onboard-logo-copy">
                  <p className="admin-login-glass-label admin-onboard-logo-label">Organization logo</p>
                  <p className="admin-onboard-logo-hint">Optional — shown in the sidebar and browser tab</p>
                  <button
                    type="button"
                    className="admin-onboard-logo-btn"
                    disabled={logoUploading}
                    onClick={() => document.getElementById("onboard-org-logo")?.click()}
                  >
                    {logoUploading ? "Uploading…" : logoUrl ? "Change logo" : "Upload logo"}
                  </button>
                </div>
                <input
                  id="onboard-org-logo"
                  type="file"
                  className="sr-only"
                  accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                  onChange={handleLogoPick}
                />
              </div>

              <button
                type="submit"
                className="admin-login-glass-submit"
                disabled={!canContinueStep1}
              >
                Continue to admin setup
              </button>

              <p className="admin-login-glass-foot">
                Already have an account?{" "}
                <Link to={ROUTES.login} className="admin-login-glass-foot-link">Sign in</Link>
              </p>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="admin-login-glass-card admin-onboard-card">
              <p className="admin-onboard-card-step">Step 2 of 2</p>
              <h2 className="admin-login-glass-card-title">Create your admin account</h2>
              <p className="admin-login-glass-card-sub">
                Workspace: <strong>{form.organization_name}</strong>
              </p>

              <div className="admin-onboard-name-row">
                <div>
                  <label className="admin-login-glass-label" htmlFor="onboard-first-name">First name</label>
                  <input
                    id="onboard-first-name"
                    type="text"
                    className="admin-login-glass-input admin-onboard-input-full"
                    value={form.first_name}
                    onChange={updateField("first_name")}
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <label className="admin-login-glass-label" htmlFor="onboard-last-name">Last name</label>
                  <input
                    id="onboard-last-name"
                    type="text"
                    className="admin-login-glass-input admin-onboard-input-full"
                    value={form.last_name}
                    onChange={updateField("last_name")}
                    required
                  />
                </div>
              </div>

              <label className="admin-login-glass-label" htmlFor="onboard-email">Work email</label>
              <input
                id="onboard-email"
                type="email"
                className="admin-login-glass-input admin-onboard-input-full"
                value={form.email}
                onChange={updateField("email")}
                placeholder="you@company.com"
                required
                autoComplete="email"
              />

              <label className="admin-login-glass-label" htmlFor="onboard-password">Password</label>
              <input
                id="onboard-password"
                type="password"
                className="admin-login-glass-input admin-onboard-input-full"
                value={form.password}
                onChange={updateField("password")}
                placeholder="At least 8 characters"
                required
                minLength={8}
                autoComplete="new-password"
              />

              <label className="admin-login-glass-label" htmlFor="onboard-confirm-password">
                Confirm password
              </label>
              <input
                id="onboard-confirm-password"
                type="password"
                className="admin-login-glass-input admin-onboard-input-full"
                value={form.confirm_password}
                onChange={updateField("confirm_password")}
                placeholder="Repeat password"
                required
                minLength={8}
                autoComplete="new-password"
              />

              {form.confirm_password && !passwordReady ? (
                <p className="admin-onboard-field-hint">Passwords must match and be at least 8 characters.</p>
              ) : null}

              <div className="admin-onboard-actions">
                <button
                  type="button"
                  className="admin-onboard-back"
                  onClick={() => setStep(1)}
                  disabled={loading}
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="admin-login-glass-submit admin-onboard-submit"
                  disabled={loading || !passwordReady}
                >
                  {loading ? "Creating workspace…" : "Create organization"}
                </button>
              </div>

              <p className="admin-login-glass-foot">
                Already have an account?{" "}
                <Link to={ROUTES.login} className="admin-login-glass-foot-link">Sign in</Link>
              </p>
            </form>
          )}
        </section>
      </main>

      <footer className="admin-login-glass-page-foot">
        Isolated workspace · 5X platform
      </footer>
    </div>
  );
}
